import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Edit,
  Trash2,
  Crown,
  Shield,
  ShieldCheck,
  X,
  Mail,
  Calendar,
  Filter,
  Activity,
  Clock,
  AlertTriangle,
  Zap,
  LogIn,
  Building2,
  ChevronDown,
  ChevronRight,
  UserPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { OverridesModal } from '../../components/Admin/OverridesModal';
import { useAuthStore } from '../../store/authStore';
import type { User } from '../../store/authStore';
import {
  EFFECTIVE_TIERS,
  isPaidTier,
  normalizeTier,
  tierLabel,
  type EffectiveTier,
} from '../../constants/planLimits';
import toast from 'react-hot-toast';

type TeamMemberRow = {
  id: string;
  name: string;
  email: string | null;
  role: 'editor' | 'viewer' | string;
};

type MemberOfRow = {
  ownerId: string;
  ownerName: string;
  ownerEmail: string | null;
  role: 'editor' | 'viewer' | string;
};

// Admin view-model for a user row. Extends the shared `User` shape
// with the extra fields `GET /api/admin/users` returns but that the
// auth store doesn't need (pageCount, hasOverrides, team…). Kept loosely
// typed so additions to the admin payload don't force a store
// refactor.
type AdminUser = User & {
  pageCount?: number;
  hasOverrides?: boolean;
  teamMemberCount?: number;
  teamSeatMax?: number;
  teamMembers?: TeamMemberRow[];
  memberOf?: MemberOfRow[];
};

function formatSeatCap(max: number | undefined): string {
  if (max === undefined || max === null) return '—';
  if (max === -1) return '∞';
  return String(max);
}

function teamRoleLabel(role: string): string {
  if (role === 'viewer') return 'Viewer';
  if (role === 'editor') return 'Editor';
  return role;
}

/** YYYY-MM-DD in Asia/Bangkok for `<input type="date">`. */
function toBangkokDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function bangkokDatePlusDays(days: number): string {
  const now = Date.now() + days * 24 * 60 * 60 * 1000;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(now));
}

/**
 * Per-tier presentation lookup. Centralised so the badge in the table,
 * the stat card on top, and the upgrade-toggle button all stay
 * visually consistent — change here, change everywhere. Mirrors the
 * Dashboard.tsx tier theme (blue=pro, purple=agency, slate=enterprise)
 * so a user landing in Admin sees the same colour story they see on
 * their own dashboard.
 */
const TIER_THEME: Record<
  EffectiveTier,
  {
    badgeClass: string; // pill bg + text + shadow
    icon: React.ComponentType<{ className?: string }>;
    statIconClass: string; // colour for the stat-card icon
  }
> = {
  free: {
    badgeClass: 'bg-gray-100 text-gray-500',
    icon: Users,
    statIconClass: 'text-gray-400',
  },
  pro: {
    badgeClass:
      'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-sm shadow-blue-200',
    icon: Zap,
    statIconClass: 'text-blue-500',
  },
  agency: {
    badgeClass:
      'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm shadow-purple-200',
    icon: Crown,
    statIconClass: 'text-purple-500',
  },
  enterprise: {
    badgeClass:
      'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-sm shadow-amber-200',
    icon: Building2,
    statIconClass: 'text-amber-500',
  },
};

/**
 * Render the plan badge for a user. Always normalises through
 * normalizeTier() so a legacy 'plus' row coming back from the API
 * (or any unexpected string) renders as a sensible Pro badge instead
 * of falling through to the default grey "Free" — that mismatch
 * caused the original bug where Enterprise admins still showed Free.
 */
function TierBadge({ subscription }: { subscription: string }) {
  const tier = normalizeTier(subscription);
  const theme = TIER_THEME[tier];
  const Icon = theme.icon;
  if (tier === 'free') {
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${theme.badgeClass}`}>
        Free
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${theme.badgeClass}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {tierLabel(subscription)}
    </span>
  );
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, token, impersonate } = useAuthStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [overridesUser, setOverridesUser] = useState<AdminUser | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | EffectiveTier>('all');
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const toggleTeamExpand = (userId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const filteredUsers = users.filter((u) => {
    const q = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !q ||
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.teamMembers || []).some(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.email || '').toLowerCase().includes(q)
      ) ||
      (u.memberOf || []).some(
        (o) =>
          o.ownerName.toLowerCase().includes(q) ||
          (o.ownerEmail || '').toLowerCase().includes(q)
      );

    // Normalise both sides so legacy 'plus' rows are matched by the
    // 'pro' filter (they're functionally Pro after migration 020).
    const matchesFilter =
      filterStatus === 'all' || normalizeTier(u.subscription) === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const orgsWithMembers = users.filter((u) => (u.teamMemberCount || 0) > 0).length;
  const usersInTeams = users.filter((u) => (u.memberOf || []).length > 0).length;

  // Helper: ตรวจสอบว่า subscription หมดอายุแล้วหรือยัง
  const isExpired = (user: User) => {
    if (!user.subscriptionExpiresAt) return false;
    return new Date(user.subscriptionExpiresAt) < new Date();
  };

  // Helper: format วันหมดอายุ
  const formatExpiryDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Helper: คำนวณจำนวนวันที่เหลือ
  const daysRemaining = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const now = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Bucket users by their EFFECTIVE tier (so legacy 'plus' rolls into
  // the 'pro' bucket — same logic the FE uses everywhere else). Counts
  // for every tier in EFFECTIVE_TIERS even if it's zero, so the stat
  // grid stays consistent: an empty Enterprise bucket still gets a
  // card showing "0", which is what the operator wants to see.
  const tierCounts: Record<EffectiveTier, number> = {
    free: 0, pro: 0, agency: 0, enterprise: 0,
  };
  users.forEach((u) => {
    tierCounts[normalizeTier(u.subscription)] += 1;
  });
  const adminCount = users.filter((u) => u.role === 'admin').length;

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
  };

  const handleSaveUser = async (updatedUser: AdminUser) => {
    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/users/${updatedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: updatedUser.name,
          email: updatedUser.email,
          subscription: updatedUser.subscription,
          role: updatedUser.role,
          subscriptionExpiresAt: isPaidTier(updatedUser.subscription)
            ? updatedUser.subscriptionExpiresAt || null
            : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const saved = await response.json().catch(() => null);
      const nextUser: AdminUser = {
        ...updatedUser,
        subscriptionExpiresAt: isPaidTier(updatedUser.subscription)
          ? saved?.subscriptionExpiresAt ?? updatedUser.subscriptionExpiresAt ?? null
          : null,
      };
      setUsers(users.map((u) => (u.id === updatedUser.id ? nextUser : u)));
      setEditingUser(null);
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteClick = (user: AdminUser) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleImpersonate = async (user: AdminUser) => {
    if (user.id === currentUser?.id) {
      toast.error('Cannot impersonate yourself');
      return;
    }
    try {
      await impersonate(user.id);
      toast.success(`Logged in as ${user.name || user.email}`);
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message || 'Failed to login as user');
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    if (userToDelete.role === 'admin') {
      toast.error("Cannot delete an admin user.");
      setShowDeleteModal(false);
      setUserToDelete(null);
      return;
    }

    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      setUsers(users.filter(u => u.id !== userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
      toast.success('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const handleToggleSubscription = async (user: AdminUser) => {
    if (user.role === 'admin') {
      toast.error("Cannot change subscription for an admin user.");
      return;
    }

    // The quick-toggle button is a "make this user a paying customer
    // RIGHT NOW" shortcut. Default to 'pro' (cheapest paid tier) so a
    // careless click can't accidentally hand out Agency/Enterprise
    // entitlements. Operators wanting Agency/Enterprise must use the
    // Edit modal which has the full picker. The flip direction:
    //   free → pro    (grant)
    //   any paid → free (revoke, regardless of which paid tier)
    const newSubscription = isPaidTier(user.subscription) ? 'free' : 'pro';

    try {
      if (!token) return;
      const response = await fetch(`${API_URL}/admin/users/${user.id}/subscription`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription: newSubscription }),
      });

      if (!response.ok) {
        throw new Error('Failed to update subscription');
      }

      const updatedUser = { ...user, subscription: newSubscription };
      setUsers(users.map(u => (u.id === updatedUser.id ? updatedUser : u)));
      toast.success(`User ${user.name}'s plan changed to ${newSubscription.toUpperCase()}`);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Activity className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading users...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-600" />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">Manage all users and their subscriptions</p>
        </div>

        {/* Stats — Total, one card per effective tier, Admins, then team orgs. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          {EFFECTIVE_TIERS.map((tier) => {
            const theme = TIER_THEME[tier];
            const Icon = theme.icon;
            return (
              <div
                key={tier}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {tierLabel(tier)} Users
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{tierCounts[tier]}</p>
                  </div>
                  <Icon className={`w-8 h-8 ${theme.statIconClass}`} />
                </div>
              </div>
            );
          })}

          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Admins</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{adminCount}</p>
              </div>
              <Shield className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Orgs w/ Team</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{orgsWithMembers}</p>
                <p className="text-xs text-gray-400 mt-0.5">{usersInTeams} members</p>
              </div>
              <UserPlus className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[300px] relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | EffectiveTier)}
                className="px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Plans</option>
                {EFFECTIVE_TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tierLabel(tier)} Only
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Team / Org</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Member Since</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const memberCount = user.teamMemberCount || 0;
                  const seatMax = user.teamSeatMax;
                  const memberOf = user.memberOf || [];
                  const teamMembers = user.teamMembers || [];
                  const canExpand = memberCount > 0;
                  const isExpanded = expandedTeams.has(user.id);

                  return (
                    <React.Fragment key={user.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4 shrink-0" />
                            {user.email}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <TierBadge subscription={user.subscription} />
                              {user.hasOverrides && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                                  title="Admin override active — click Overrides to edit"
                                >
                                  <ShieldCheck className="w-3 h-3" />
                                  Override
                                </span>
                              )}
                              {user.role !== 'admin' && (
                                <button
                                  onClick={() => handleToggleSubscription(user)}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title={
                                    isPaidTier(user.subscription)
                                      ? 'Downgrade to Free'
                                      : 'Upgrade to Pro (use Edit for Agency / Enterprise)'
                                  }
                                >
                                  {isPaidTier(user.subscription) ? (
                                    <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                  ) : (
                                    <Crown className="w-4 h-4 text-purple-500 hover:text-purple-700" />
                                  )}
                                </button>
                              )}
                            </div>
                            {isPaidTier(user.subscription) && user.subscriptionExpiresAt && (
                              <div className="flex items-center gap-1 ml-0.5">
                                {isExpired(user) ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                                    <AlertTriangle className="w-3 h-3" />
                                    หมดอายุแล้ว ({formatExpiryDate(user.subscriptionExpiresAt)})
                                  </span>
                                ) : (daysRemaining(user.subscriptionExpiresAt) ?? 0) <= 7 ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                                    <Clock className="w-3 h-3" />
                                    เหลือ {daysRemaining(user.subscriptionExpiresAt)} วัน ({formatExpiryDate(user.subscriptionExpiresAt)})
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                    <Clock className="w-3 h-3" />
                                    หมดอายุ {formatExpiryDate(user.subscriptionExpiresAt)}
                                  </span>
                                )}
                              </div>
                            )}
                            {isPaidTier(user.subscription) && !user.subscriptionExpiresAt && (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 ml-0.5">
                                <Clock className="w-3 h-3" />
                                ไม่มีวันหมดอายุ
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5 min-w-[180px]">
                            {canExpand ? (
                              <button
                                type="button"
                                onClick={() => toggleTeamExpand(user.id)}
                                className="inline-flex items-center gap-1.5 text-left text-sm font-medium text-indigo-700 hover:text-indigo-900"
                                title="Show team members"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 shrink-0" />
                                )}
                                <Building2 className="w-3.5 h-3.5 shrink-0" />
                                <span>
                                  Owner · {memberCount}/{formatSeatCap(seatMax)} seats
                                </span>
                              </button>
                            ) : seatMax && seatMax !== 0 ? (
                              <span className="inline-flex items-center gap-1.5 text-sm text-gray-400">
                                <Building2 className="w-3.5 h-3.5" />
                                Owner · 0/{formatSeatCap(seatMax)} seats
                              </span>
                            ) : null}

                            {memberOf.length > 0 && (
                              <div className="flex flex-col gap-1">
                                {memberOf.map((org) => (
                                  <span
                                    key={org.ownerId}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-700"
                                    title={org.ownerEmail || undefined}
                                  >
                                    <UserPlus className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                    <span className="font-medium truncate max-w-[140px]">
                                      {org.ownerName}
                                    </span>
                                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">
                                      {teamRoleLabel(org.role)}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}

                            {!canExpand && memberOf.length === 0 && (!seatMax || seatMax === 0) && (
                              <span className="text-sm text-gray-300">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.role === 'admin' ? (
                            <span className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1 w-fit">
                              <Shield className="w-3 h-3" />
                              Admin
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">User</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handleImpersonate(user)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Login as this user"
                            >
                              <LogIn className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setOverridesUser(user)}
                              className={`p-2 rounded-lg transition-colors ${
                                user.hasOverrides
                                  ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                                  : 'text-amber-600 hover:bg-amber-50'
                              }`}
                              title={user.hasOverrides ? 'Edit cap overrides (active)' : 'Set cap overrides'}
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && canExpand && (
                        <tr className="bg-indigo-50/40">
                          <td colSpan={7} className="px-6 py-3">
                            <div className="rounded-lg border border-indigo-100 bg-white/80 px-4 py-3">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                Team of {user.name} · {memberCount} member{memberCount === 1 ? '' : 's'}
                                {seatMax !== undefined && seatMax !== 0
                                  ? ` (cap ${formatSeatCap(seatMax)})`
                                  : ''}
                              </p>
                              <ul className="divide-y divide-gray-100">
                                {teamMembers.map((m) => (
                                  <li
                                    key={m.id}
                                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
                                        {(m.name || '?').charAt(0).toUpperCase()}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="font-medium text-gray-900 truncate">{m.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{m.email || '—'}</div>
                                      </div>
                                    </div>
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700">
                                      {teamRoleLabel(m.role)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No users found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingUser(null)}>
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit User</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subscription</label>
                <select
                  value={normalizeTier(editingUser.subscription)}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, subscription: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={editingUser.role === 'admin'}
                >
                  {EFFECTIVE_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tierLabel(tier)}
                    </option>
                  ))}
                </select>
                {editingUser.role === 'admin' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Admin users cannot have their subscription changed from this dialog.
                  </p>
                )}
              </div>
              {isPaidTier(editingUser.subscription) && editingUser.role !== 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package expiry
                  </label>
                  <input
                    type="date"
                    value={toBangkokDateInput(editingUser.subscriptionExpiresAt)}
                    min={bangkokDatePlusDays(0)}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        subscriptionExpiresAt: e.target.value || null,
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      { label: '30 วัน', days: 30 },
                      { label: '90 วัน', days: 90 },
                      { label: '1 ปี', days: 365 },
                    ].map((preset) => (
                      <button
                        key={preset.days}
                        type="button"
                        onClick={() =>
                          setEditingUser({
                            ...editingUser,
                            subscriptionExpiresAt: bangkokDatePlusDays(preset.days),
                          })
                        }
                        className="px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        {preset.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setEditingUser({ ...editingUser, subscriptionExpiresAt: null })
                      }
                      className="px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      ไม่มีวันหมดอายุ
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingUser.subscriptionExpiresAt
                      ? 'แพ็กเกจจะหมดอายุสิ้นวันตามปฏิทินกรุงเทพฯ'
                      : 'เว้นว่างไว้ = ให้ใช้แพ็กเกจนี้ได้ตลอด (ไม่มีวันหมดอายุ)'}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as 'user' | 'admin' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={editingUser.id === currentUser?.id}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => setEditingUser(null)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveUser(editingUser)}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 6 Overrides Modal */}
      {overridesUser && (
        <OverridesModal
          user={{
            id: overridesUser.id,
            email: overridesUser.email,
            name: overridesUser.name,
            subscription: overridesUser.subscription,
          }}
          onClose={() => setOverridesUser(null)}
          onSaved={() => {
            // Re-fetch so the badge + hasOverrides reflect the new
            // state without waiting for the next page refresh.
            fetchUsers();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete user <span className="font-semibold text-gray-900">{userToDelete.name}</span> ({userToDelete.email})? This action cannot be undone.
            </p>
            {userToDelete.role === 'admin' && (
              <p className="text-red-600 text-sm mb-4 flex items-center gap-2 bg-red-50 p-3 rounded-lg">
                <Shield className="w-4 h-4" /> Admin users cannot be deleted.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className={`px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors ${userToDelete.role === 'admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={userToDelete.role === 'admin'}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
